import React from 'react';
import { Action, useNotifications } from '@doveit/bricks';
import Wizard from '../../../containers/wizard/Wizard';
import { WizardStep } from '../../../containers/wizard/types';
import { Agency } from '../../../providers/api/dtos';
import { agencyRegistryWizardStepSchema } from './registry-step/AgencyRegistryWizardStep.schema';
import AgencyRegistryWizardStep from './registry-step/AgencyRegistryWizardStep';
import AgencyDescriptionWizardStep from './description-step/AgencyDescriptionWizardStep';
import { createAgency, updateAgency } from '../../../providers/api/agency/agencyProvider';
import AgencyTeamWizardStep from './team-step/AgencyTeamWizardStep';
import AgencyMembershipContractWizardStep from './membership-contract-step/AgencyMembershipContractWizardStep';
import AgencyCommissionsWizardStep from './commissions-step/AgencyCommissionsWizardStep';
import AgencyCoordinatorsWizardStep from './coordinators-step/AgencyCoordinatorsWizardStep';

export interface CreateAgencyWizardProps extends React.AriaAttributes {
  onFinish?: (agency: Agency) => void,
}

export const CREATE_AGENCY_SUCCESS_MESSAGE = "L'agenzia è stata creata con successo";
export const CREATE_AGENCY_ERROR_MESSAGE = "Non è stato possibile creare l'agenzia";
export const UPDATE_AGENCY_ERROR_MESSAGE = "Non è stato possibile aggiornare l'agenzia";

const CreateAgencyWizard: React.FC<CreateAgencyWizardProps> = ({
  onFinish,
  ...rest
}) => {
  const [existingAgency, setExistingAgency] = React.useState<Agency>();
  const { addSuccess } = useNotifications();

  const handleUpdateStepSubmit = React.useCallback(async (agency: Agency): Promise<Agency> => {
    try {
      const updatedAgency = await updateAgency(existingAgency!.id!, agency);

      return updatedAgency;
    } catch (error) {
      throw new Error(UPDATE_AGENCY_ERROR_MESSAGE);
    }
  }, [existingAgency]);

  const steps = React.useMemo<WizardStep<Agency, Agency>[]>(() => ([
    // 1. Anagrafica
    {
      navigation: { title: 'Anagrafica' },
      validationSchema: agencyRegistryWizardStepSchema,
      onSubmit: async (agency): Promise<Agency> => {
        try {
          const upsertedAgency = existingAgency?.id
            ? await updateAgency(existingAgency.id, agency)
            : await createAgency(agency);

          setExistingAgency(upsertedAgency);
          addSuccess(CREATE_AGENCY_SUCCESS_MESSAGE);

          return upsertedAgency;
        } catch (error) {
          throw new Error(CREATE_AGENCY_ERROR_MESSAGE);
        }
      },
      render: (props) => (
        <AgencyRegistryWizardStep {...props} />
      ),
      renderSkipStepAction: ({ skipStep, isSubmitting }) => (
        <Action
          emphasis="high"
          label="Salva ed esci"
          aria-label="Salva ed esci"
          onClick={() => skipStep('completed', true)}
          disabled={isSubmitting}
        />
      ),
    },
    // 2. Descrizione
    {
      navigation: {
        title: 'Descrizione',
        disabled: !existingAgency,
      },
      onSubmit: handleUpdateStepSubmit,
      render: (props) => <AgencyDescriptionWizardStep {...props} />,
    },
    // 3. Firmatario e conto
    {
      navigation: {
        title: 'Firmatario e conto',
        disabled: !existingAgency,
      },
      onSubmit: handleUpdateStepSubmit,
      render: (props) => <AgencyMembershipContractWizardStep {...props} />,
    },
    // 4. Provvigioni
    {
      navigation: {
        title: 'Provvigioni',
        disabled: !existingAgency,
      },
      onSubmit: handleUpdateStepSubmit,
      render: (props) => <AgencyCommissionsWizardStep {...props} />,
      renderNextStepAction: !existingAgency?.franchising ? false : undefined,
      renderSkipStepAction: ({ skipStep, isSubmitting, currentStep }) => (
        <Action
          emphasis="high"
          color={!existingAgency?.franchising ? 'primary' : 'neutral'}
          label={!existingAgency?.franchising ? 'Continua' : 'Salta'}
          aria-label={!existingAgency?.franchising ? 'Continua' : 'Salta'}
          onClick={() => skipStep((typeof currentStep === 'number' && currentStep < steps.length - 1)
            ? currentStep + 1
            : 'completed', false)}
          disabled={isSubmitting}
        />
      ),
    },
    // 5. Team
    {
      navigation: {
        title: 'Team',
        disabled: !existingAgency,
      },
      onSubmit: handleUpdateStepSubmit,
      render: (props) => <AgencyTeamWizardStep {...props} />,
      renderSkipStepAction: ({ skipStep, isSubmitting, currentStep }) => {
        const hasAtLeastOneMember = existingAgency && existingAgency.agentIds.length > 0;

        return (
          <Action
            emphasis="high"
            label={hasAtLeastOneMember ? 'Salta' : 'Salta ed esci'}
            aria-label={hasAtLeastOneMember ? 'Salta' : 'Salta ed esci'}
            onClick={() => skipStep((hasAtLeastOneMember && typeof currentStep === 'number' && currentStep < steps.length - 1)
              ? currentStep + 1
              : 'completed', false)}
            disabled={isSubmitting}
          />
        );
      },
    },
    // 6. Coordinatore
    {
      navigation: {
        title: 'Coordinatore',
        disabled: !existingAgency || existingAgency.agentIds.length === 0,
      },
      onSubmit: handleUpdateStepSubmit,
      render: (props) => <AgencyCoordinatorsWizardStep {...props} />,
    },
  ]), [addSuccess, existingAgency, handleUpdateStepSubmit]);

  return (
    <Wizard
      steps={steps}
      initialData={existingAgency}
      validationStrategy="progressive"
      onFinish={onFinish}
      {...rest}
    />
  );
};

export default CreateAgencyWizard;
